import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

function prepareQuickBuyVariantPicker(root) {
  const variantPicker = root.querySelector('variant-picker');

  if (!variantPicker) return;

  const fieldsets = Array.from(
    variantPicker.querySelectorAll('fieldset.variant-option')
  );

  for (const fieldset of fieldsets) {
    const inputs = Array.from(
      fieldset.querySelectorAll('input[type="radio"][data-option-name]')
    );

    if (!inputs.length) continue;

    const optionLabel = inputs[0]?.dataset.optionName || '';
    const optionName = optionLabel.trim().toLowerCase();

    /* ================================
       COLOUR / COLOR IMAGE SWATCHES
       ================================ */

    if (optionName === 'colour' || optionName === 'color') {
      fieldset.classList.add('quick-buy-color-swatches');

      for (const input of inputs) {
        const label = input.closest('.variant-option__button-label');

        if (!label) continue;

        const imageUrl = input.dataset.variantImage;

        if (!imageUrl) continue;

        let image = label.querySelector('.quick-buy-variant-image');

        if (!image) {
          image = document.createElement('span');
          image.className = 'quick-buy-variant-image';

          const existingSwatch = label.querySelector('.swatch');

          if (existingSwatch) {
            existingSwatch.insertAdjacentElement('beforebegin', image);
          } else {
            input.insertAdjacentElement('afterend', image);
          }
        }

        image.style.backgroundImage = `url("${imageUrl}")`;
      }

      continue;
    }

    /* ================================
       SIZE DROPDOWN
       ================================ */

    if (optionName === 'size') {
      const dropdown = document.createElement('div');

      dropdown.className =
        'variant-option variant-option--dropdowns quick-buy-size-dropdown';

      const productId = variantPicker.dataset.productId || 'product';
      const selectId = `QuickBuySize-${productId}`;

      const dropdownLabel = document.createElement('label');

      dropdownLabel.htmlFor = selectId;
      dropdownLabel.textContent = optionLabel;

      const selectWrapper = document.createElement('div');

      selectWrapper.className = 'variant-option__select-wrapper';

      const select = document.createElement('select');

      select.id = selectId;
      select.name = `options[${optionLabel}]`;
      select.className =
        'variant-option__select quick-buy-size-select';

      for (const input of inputs) {
        const option = document.createElement('option');

        option.value = input.value;

        const available = input.dataset.optionAvailable !== 'false';

        option.textContent = available
          ? input.value
          : `${input.value} - Unavailable`;

        if (input.dataset.inputId) {
          option.dataset.inputId = input.dataset.inputId;
        }

        if (input.dataset.optionName) {
          option.dataset.optionName = input.dataset.optionName;
        }

        if (input.dataset.optionValueId) {
          option.dataset.optionValueId = input.dataset.optionValueId;
        }

        if (input.dataset.variantId) {
          option.dataset.variantId = input.dataset.variantId;
        }

        if (input.dataset.connectedProductUrl) {
          option.dataset.connectedProductUrl =
            input.dataset.connectedProductUrl;
        }

        if (
          input.checked ||
          input.dataset.currentChecked === 'true'
        ) {
          option.selected = true;
          option.setAttribute('selected', 'selected');
        }

        select.appendChild(option);
      }

      selectWrapper.appendChild(select);

      dropdown.appendChild(dropdownLabel);
      dropdown.appendChild(selectWrapper);

      fieldset.replaceWith(dropdown);
    }
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest('product-hotspot-component')
      );

    const productLink =
      productCard?.getProductCardLink() ||
      hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();

    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();

    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    ) {
      return;
    }

    const productOptionsCount =
      this.dataset.productOptionsCount;

    const quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById('quick-add-modal-content');

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector('variant-picker')
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(newHtml);
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    let productGrid =
      this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html =
        await this.fetchProductPage(currentUrl);

      if (html) {
        const gridElement =
          html.querySelector('[data-product-grid-content]');

        if (gridElement) {
          productGrid =
            /** @type {Element} */ (
              gridElement.cloneNode(true)
            );

          this.#cachedContent.set(
            currentUrl,
            productGrid
          );
        }
      }
    }

    if (productGrid) {
      const freshContent =
        /** @type {Element} */ (
          productGrid.cloneNode(true)
        );

      await this.updateQuickAddModal(
        freshContent
      );

      this.#updateVariantPicker(
        productGrid
      );

      const modalContent =
        document.getElementById(
          'quick-add-modal-content'
        );

      if (modalContent) {
        prepareQuickBuyVariantPicker(
          modalContent
        );
      }
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () =>
        this.toggleAttribute(
          'stay-visible',
          false
        ),
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    try {
      const response = await fetch(
        productPageUrl,
        {
          signal:
            this.#abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      const html =
        new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!productGrid || !modalContent) {
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(
      modalContent,
      productGrid
    );

    this.#syncVariantSelection(
      modalContent
    );
  }

  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const anchorElement =
          /** @type {HTMLAnchorElement} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        if (anchorElement) {
          if (viewMoreDetailsLink) {
            viewMoreDetailsLink.href =
              anchorElement.href;
          }

          if (mobileProductTitle) {
            mobileProductTitle.href =
              anchorElement.href;
          }
        }

        prepareQuickBuyVariantPicker(
          this
        );
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 &&
        iosVersion.minor >= 4)
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (grid) {
        const currentWidth =
          grid.getBoundingClientRect().width;

        grid.style.width =
          `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}